import React, { useState } from 'react';
import { MasterConfig as SiteConfig, PageConfig, ProductPlan } from 'shared-types';
import ThemeEditor from './ThemeEditor';
import NavigationEditor from './NavigationEditor';
import ServerListEditor from './ServerListEditor';
import PageEditor from './PageEditor';
import ProductListEditor from './ProductListEditor';

interface SiteEditorProps {
    siteId: string;
    config: SiteConfig;
    onChange: (newConfig: SiteConfig) => void;
}

const SectionWrapper: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-8">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">{title}</h3>
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

type TeezNetEditView = 'list' | 'page' | 'products';

const SiteEditor: React.FC<SiteEditorProps> = ({ siteId, config, onChange }) => {
    const [editView, setEditView] = useState<TeezNetEditView>('list');
    const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

    const handleThemeChange = (newTheme: SiteConfig['theme']) => {
        onChange({ ...config, theme: newTheme });
    };

    const handleNavChange = (newNav: SiteConfig['navigation']) => {
        onChange({ ...config, navigation: newNav });
    };
    
    const handleServerChange = (newServers: SiteConfig['testConfig']['servers']) => {
        onChange({ ...config, testConfig: { ...config.testConfig, servers: newServers } });
    };
    
    const handlePageChange = (updatedPage: PageConfig) => {
        const newPages = config.pages?.map(p => p.id === updatedPage.id ? updatedPage : p);
        onChange({ ...config, pages: newPages });
    };
    
    const handleProductsChange = (newProducts: ProductPlan[]) => {
        onChange({ ...config, products: newProducts });
    };
    
    const selectedPage = config.pages?.find(p => p.id === selectedPageId);

    if (siteId === 'teeznet') {
        if (editView === 'page' && selectedPage) {
            return (
                <PageEditor 
                    page={selectedPage} 
                    onChange={handlePageChange} 
                    onBack={() => setEditView('list')}
                />
            );
        }
        
        if (editView === 'products' && config.products) {
            return (
                 <ProductListEditor
                    products={config.products}
                    onChange={handleProductsChange}
                    onBack={() => setEditView('list')}
                />
            )
        }

        return (
            <div className="p-6">
                 <SectionWrapper title="Site Theme & Appearance">
                    <ThemeEditor theme={config.theme} onChange={handleThemeChange} />
                </SectionWrapper>
                {config.navigation && (
                    <SectionWrapper title="Site Navigation">
                        <NavigationEditor navigation={config.navigation} onChange={handleNavChange} />
                    </SectionWrapper>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {config.pages && (
                        <SectionWrapper title="Page Content">
                            <div className="space-y-2">
                                {config.pages.map(page => (
                                    <button key={page.id} onClick={() => { setSelectedPageId(page.id); setEditView('page'); }} className="w-full text-left p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition">
                                        <p className="font-semibold">{page.id.charAt(0).toUpperCase() + page.id.slice(1)} Page</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Path: {page.path}</p>
                                    </button>
                                ))}
                            </div>
                        </SectionWrapper>
                    )}
                    {config.products && (
                         <SectionWrapper title="Global Content">
                           <button onClick={() => setEditView('products')} className="w-full text-left p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition">
                                <p className="font-semibold">Product Plans</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Manage all internet packages</p>
                            </button>
                        </SectionWrapper>
                    )}
                </div>
            </div>
        );
    }

    // Fallback for speedtest app
    return (
        <div className="p-6">
            <SectionWrapper title="Site Theme & Appearance">
                <ThemeEditor theme={config.theme} onChange={handleThemeChange} />
            </SectionWrapper>
            
            {config.testConfig?.servers && (
                 <SectionWrapper title="Speed Test Servers">
                    <ServerListEditor servers={config.testConfig.servers} onChange={handleServerChange} />
                </SectionWrapper>
            )}
        </div>
    );
};

export default SiteEditor;